import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { Award as A1, DollarSign as A2, Calendar as A3, Filter as A4, FileText as A5, CheckCircle as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, Filter, DollarSign, GraduationCap, Clock, ExternalLink,
  BookmarkPlus, BookmarkCheck, X, ChevronDown, Star, TrendingUp,
  MapPin, Award, Briefcase, Shield, Users, Wrench, Heart, Rocket,
  CheckCircle, AlertCircle, FileText, ArrowUpDown, Info, Sparkles,
  ChevronRight, Globe, Bookmark
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'ALL','AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const SCHOLARSHIP_TYPES = [
  { key: '', label: 'All Types' },
  { key: 'stem', label: 'STEM' },
  { key: 'merit', label: 'Merit-Based' },
  { key: 'need', label: 'Need-Based' },
  { key: 'arts', label: 'Arts & Creative' },
  { key: 'leadership', label: 'Leadership' },
  { key: 'community_service', label: 'Community Service' },
  { key: 'athletics', label: 'Athletics' },
  { key: 'military', label: 'Military' },
  { key: 'first_generation', label: 'First Generation' },
  { key: 'trade', label: 'Trade / Vocational' },
  { key: 'entrepreneur', label: 'Entrepreneurship' },
];

const SORT_OPTIONS = [
  { key: 'relevance', label: 'Best Match' },
  { key: 'amount_high', label: 'Highest Award' },
  { key: 'amount_low', label: 'Lowest Award' },
  { key: 'deadline', label: 'Soonest Deadline' },
];

const TRACK_STATUSES = [
  { key: 'saved', label: 'Saved', color: 'slate' },
  { key: 'in_progress', label: 'In Progress', color: 'amber' },
  { key: 'applied', label: 'Applied', color: 'blue' },
  { key: 'submitted', label: 'Submitted', color: 'emerald' },
];

function MatchBadge({ score }) {
  const color = score >= 70 ? 'emerald' : score >= 40 ? 'amber' : 'slate';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-500/20 text-${color}-400`} data-testid="match-badge">
      <Star className="w-3 h-3" /> {score}% match
    </span>
  );
}

function ScholarshipCard({ sch, savedMap, onSave, onUnsave, onSelect, onTrack }) {
  const isSaved = savedMap[sch.id];
  const tracking = isSaved?.status;
  const match = sch.match || {};
  const amt = sch.award_amount_max ? `$${sch.award_amount_max.toLocaleString()}` : 'Varies';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5 hover:border-blue-500/30 transition-all group cursor-pointer"
      onClick={() => onSelect(sch)}
      data-testid={`scholarship-card-${sch.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-base group-hover:text-blue-400 transition-colors truncate">
            {sch.scholarship_name}
          </h3>
          <p className="text-slate-300 text-xs mt-0.5 truncate">{sch.provider_name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {match.overall_score > 0 && <MatchBadge score={match.overall_score} />}
          <button
            onClick={(e) => { e.stopPropagation(); isSaved ? onUnsave(sch.id) : onSave(sch.id); }}
            className={`p-1.5 rounded-lg transition-colors ${isSaved ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-800 text-slate-300 hover:text-blue-400'}`}
            data-testid={`save-btn-${sch.id}`}
          >
            {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <BookmarkPlus className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <p className="text-slate-300 text-sm line-clamp-2 mb-3">{sch.description}</p>

      <div className="flex flex-wrap gap-2 mb-3">
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-md text-xs">
          <DollarSign className="w-3 h-3" /> {amt}
        </span>
        {sch.state_scope === 'national' && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-400 rounded-md text-xs">
            <Globe className="w-3 h-3" /> National
          </span>
        )}
        {sch.state_scope === 'state_specific' && sch.eligible_states?.[0] && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-400 rounded-md text-xs">
            <MapPin className="w-3 h-3" /> {sch.eligible_states.join(', ')}
          </span>
        )}
        {sch.stem_flag && <span className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded-md text-xs">STEM</span>}
        {sch.arts_flag && <span className="px-2 py-1 bg-pink-500/10 text-pink-400 rounded-md text-xs">Arts</span>}
        {sch.need_based && <span className="px-2 py-1 bg-amber-500/10 text-amber-400 rounded-md text-xs">Need-Based</span>}
        {sch.merit_based && <span className="px-2 py-1 bg-emerald-500/10 text-emerald-300 rounded-md text-xs">Merit</span>}
        {sch.trade_school_flag && <span className="px-2 py-1 bg-orange-500/10 text-orange-400 rounded-md text-xs">Trade</span>}
        {sch.first_generation_flag && <span className="px-2 py-1 bg-violet-500/10 text-violet-400 rounded-md text-xs">1st Gen</span>}
      </div>

      {match.match_reasons?.length > 0 && (
        <div className="flex items-start gap-1.5 mb-2">
          <Sparkles className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-amber-400/80 text-xs">{match.match_reasons[0]}</p>
        </div>
      )}

      {tracking && (
        <div className="mt-2 pt-2 border-t border-slate-800">
          <span className={`text-xs px-2 py-0.5 rounded-full bg-${TRACK_STATUSES.find(t=>t.key===tracking)?.color || 'slate'}-500/20 text-${TRACK_STATUSES.find(t=>t.key===tracking)?.color || 'slate'}-400`}>
            {TRACK_STATUSES.find(t=>t.key===tracking)?.label || tracking}
          </span>
        </div>
      )}

      <p className="text-slate-300 text-[10px] mt-2 text-right">Click for details</p>
    </motion.div>
  );
}

function ScholarshipDetailModal({ sch, savedMap, onClose, onSave, onUnsave, onTrack }) {
  if (!sch) return null;
  const match = sch.match || {};
  const isSaved = savedMap[sch.id];
  const amtMin = sch.award_amount_min ? `$${sch.award_amount_min.toLocaleString()}` : '';
  const amtMax = sch.award_amount_max ? `$${sch.award_amount_max.toLocaleString()}` : 'Varies';
  const amtDisplay = amtMin && amtMax && amtMin !== amtMax ? `${amtMin} - ${amtMax}` : amtMax;

  const checklist = sch.application_checklist || [];
  if (sch.essay_required && !checklist.find(c => c.type === 'essay')) checklist.push({ item: 'Essay required', type: 'essay' });
  if (sch.recommendation_required && !checklist.find(c => c.type === 'recommendation')) checklist.push({ item: 'Letter of recommendation', type: 'recommendation' });
  if (sch.transcript_required && !checklist.find(c => c.type === 'transcript')) checklist.push({ item: 'Official transcript', type: 'transcript' });
  if (sch.gpa_minimum > 0 && !checklist.find(c => c.type === 'gpa')) checklist.push({ item: `Minimum GPA: ${sch.gpa_minimum}`, type: 'gpa' });

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1729]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="scholarship-detail-modal"
      >
        {/* Header */}
        <div className="p-5 bg-gradient-to-r from-blue-600/15 to-purple-600/10 border-b border-slate-800">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">{sch.scholarship_name}</h2>
              <p className="text-slate-300 text-sm mt-1">{sch.provider_name}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {match.overall_score > 0 && <MatchBadge score={match.overall_score} />}
                <span className="text-emerald-400 text-sm font-mono font-bold">{amtDisplay}</span>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:text-white shrink-0">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto max-h-[calc(85vh-200px)] space-y-5">
          <p className="text-slate-300 leading-relaxed">{sch.description}</p>

          {/* Why this matches */}
          {match.match_reasons?.length > 0 && (
            <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
              <h4 className="text-amber-400 text-sm font-semibold flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4" /> Why This Matches You
              </h4>
              <ul className="space-y-1">
                {match.match_reasons.map((r, i) => (
                  <li key={i} className="text-amber-300/80 text-sm flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-0.5 shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Info Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-800/40 rounded-lg">
              <p className="text-slate-300 text-xs mb-1">Award Amount</p>
              <p className="text-emerald-400 font-mono font-bold">{amtDisplay}</p>
            </div>
            <div className="p-3 bg-slate-800/40 rounded-lg">
              <p className="text-slate-300 text-xs mb-1">Deadline</p>
              <p className="text-white text-sm">{sch.deadline_date || 'See provider website'}</p>
              <p className="text-slate-300 text-[10px]">{sch.deadline_type === 'annual_recurring' ? 'Recurring annually' : sch.deadline_type}</p>
            </div>
            <div className="p-3 bg-slate-800/40 rounded-lg">
              <p className="text-slate-300 text-xs mb-1">Scope</p>
              <p className="text-white text-sm capitalize">{sch.state_scope?.replace('_', ' ')}</p>
              {sch.eligible_states?.length > 0 && <p className="text-slate-300 text-xs">{sch.eligible_states.join(', ')}</p>}
            </div>
            <div className="p-3 bg-slate-800/40 rounded-lg">
              <p className="text-slate-300 text-xs mb-1">Renewable</p>
              <p className="text-white text-sm">{sch.recurring_or_one_time === 'renewable' ? 'Yes' : 'One-time'}</p>
            </div>
          </div>

          {/* Grade Levels */}
          {sch.grade_levels?.length > 0 && (
            <div>
              <p className="text-slate-300 text-xs font-semibold mb-2">Eligible Grade Levels</p>
              <div className="flex flex-wrap gap-1.5">
                {sch.grade_levels.map((g, i) => (
                  <span key={i} className="px-2 py-1 bg-slate-800 text-slate-300 rounded text-xs capitalize">{g.replace('_', ' ')}</span>
                ))}
              </div>
            </div>
          )}

          {/* Fields of Study */}
          {sch.field_of_study_tags?.length > 0 && (
            <div>
              <p className="text-slate-300 text-xs font-semibold mb-2">Fields of Study</p>
              <div className="flex flex-wrap gap-1.5">
                {sch.field_of_study_tags.map((f, i) => (
                  <span key={i} className="px-2 py-1 bg-blue-500/10 text-blue-300 rounded text-xs">{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Application Checklist */}
          {checklist.length > 0 && (
            <div>
              <p className="text-slate-300 text-xs font-semibold mb-2">Application Requirements</p>
              <ul className="space-y-1.5">
                {checklist.map((c, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <FileText className="w-3 h-3 text-slate-300" /> {c.item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tracking */}
          {isSaved && (
            <div>
              <p className="text-slate-300 text-xs font-semibold mb-2">Application Tracker</p>
              <div className="flex gap-2 flex-wrap">
                {TRACK_STATUSES.map(ts => (
                  <button
                    key={ts.key}
                    onClick={() => onTrack(sch.id, ts.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isSaved.status === ts.key
                        ? `bg-${ts.color}-500/30 text-${ts.color}-300 border border-${ts.color}-500/50`
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                    data-testid={`track-${ts.key}`}
                  >
                    {ts.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="flex items-start gap-2 p-3 bg-slate-800/30 rounded-lg">
            <AlertCircle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
            <p className="text-[11px] text-slate-300">
              Scholarship details including deadlines may change. Always verify current information directly with the scholarship provider before applying.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 flex gap-3">
          <Button
            onClick={() => { isSaved ? onUnsave(sch.id) : onSave(sch.id); }}
            className={isSaved ? 'bg-blue-600 hover:bg-blue-700 flex-1' : 'bg-slate-700 hover:bg-slate-600 flex-1'}
            data-testid="detail-save-btn"
          >
            {isSaved ? <><BookmarkCheck className="w-4 h-4 mr-2" /> Saved</> : <><BookmarkPlus className="w-4 h-4 mr-2" /> Save Scholarship</>}
          </Button>
          {sch.application_url && (
            <a href={sch.application_url} target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button className="btn-primary w-full" data-testid="apply-btn">
                <ExternalLink className="w-4 h-4 mr-2" /> Apply Now
              </Button>
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function ScholarshipsPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [savedMap, setSavedMap] = useState({});
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [savedList, setSavedList] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSchoolType, setFilterSchoolType] = useState('');
  const [filterGpa, setFilterGpa] = useState('');
  const [sortBy, setSortBy] = useState('relevance');

  const headers = getAuthHeader();

  // Load stats and saved on mount
  useEffect(() => {
    const init = async () => {
      try {
        const [statsRes, savedRes] = await Promise.all([
          axios.get(`${API}/scholarships/v2/stats`, headers),
          axios.get(`${API}/scholarships/v2/saved`, headers),
        ]);
        setStats(statsRes.data);
        const map = {};
        (savedRes.data.scholarships || []).forEach(s => {
          map[s.id] = { status: s.tracking?.status || 'saved' };
        });
        setSavedMap(map);
        setSavedList(savedRes.data.scholarships || []);
      } catch {}
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchScholarships = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (filterState) params.append('state', filterState);
      if (filterType) params.append('scholarship_type', filterType);
      if (filterSchoolType) params.append('school_type', filterSchoolType);
      if (filterGpa) params.append('gpa_min', filterGpa);
      params.append('sort_by', sortBy);
      params.append('page', pg);
      params.append('limit', 20);

      const res = await axios.get(`${API}/scholarships/v2/search?${params}`, headers);
      setScholarships(res.data.scholarships || []);
      setTotalResults(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
      setPage(pg);
    } catch (err) {
      toast.error('Failed to load scholarships');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterState, filterType, filterSchoolType, filterGpa, sortBy, headers]);

  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/scholarships/v2/recommendations/personalized?limit=20`, headers);
      setScholarships(res.data.recommendations || []);
      setTotalResults(res.data.total_matches || 0);
      setTotalPages(1);
    } catch {
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  // Auto-fetch on tab change
  useEffect(() => {
    if (activeTab === 'search') fetchScholarships(1);
    else if (activeTab === 'recommended') fetchRecommendations();
    else if (activeTab === 'saved') {
      axios.get(`${API}/scholarships/v2/saved`, headers).then(res => {
        setSavedList(res.data.scholarships || []);
      }).catch(() => {});
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (id) => {
    try {
      await axios.post(`${API}/scholarships/v2/save/${id}`, {}, headers);
      setSavedMap(prev => ({ ...prev, [id]: { status: 'saved' } }));
      celebrate('Saved. Money you do not pay back is the best money there is.');
    } catch { toast.error('Failed to save'); }
  };

  const handleUnsave = async (id) => {
    try {
      await axios.delete(`${API}/scholarships/v2/save/${id}`, headers);
      setSavedMap(prev => { const n = { ...prev }; delete n[id]; return n; });
      toast.success('Removed from saved');
    } catch { toast.error('Failed to remove'); }
  };

  const handleTrack = async (id, status) => {
    try {
      await axios.put(`${API}/scholarships/v2/track/${id}`, { status }, headers);
      setSavedMap(prev => ({ ...prev, [id]: { status } }));
      toast.success(`Status: ${status.replace('_', ' ')}`);
    } catch { toast.error('Failed to update status'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchScholarships(1);
  };

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="scholarships-arrival"
        eyebrow="Scholarship Finder"
        title="Money you do not have to pay back."
        subtitle="Real, verified awards matched to your situation, with the deadline, what it asks for, and how competitive it is. No spam lists, no scholarships that quietly stopped existing in 2018."
        accent="#fbbf24"
        second="#34d399"
        ctaLabel="Find my money"
        narration={"This is Scholarship Finder. Every award here is verified and still running, matched to your situation rather than dumped on you a thousand at a time. You will see the amount, the deadline, exactly what it asks for, and an honest read on how competitive it is. Save the ones worth your effort and they become dated steps in your plan, so the deadline never passes quietly."}
        facts={[
          { icon: A1, head: "Verified awards", sub: "Real programmes, still accepting applications" },
          { icon: A4, head: "Matched to you", sub: "Your state, situation, field and level" },
          { icon: A3, head: "Deadlines tracked", sub: "Saved awards become dated steps in your plan" }
        ]}
        modules={[
          { icon: A2, label: "Amount and odds", line: "What it pays, and how competitive it is", hue: "#fbbf24" },
          { icon: A5, label: "What it asks for", line: "Essays, references, transcripts and eligibility", hue: "#22d3ee" },
          { icon: A4, label: "Filter it down", line: "By amount, deadline, effort and who it is for", hue: "#a78bfa" },
          { icon: A3, label: "Deadline calendar", line: "Everything you saved, in date order", hue: "#f472b6" },
          { icon: A6, label: "Application tracker", line: "Applied, waiting, won, kept in one place", hue: "#34d399" },
          { icon: A1, label: "Local and niche", line: "The smaller awards most people never find", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader
        title="Scholarship Finder"
        description={stats ? `${stats.total_active} scholarships worth $${(stats.total_value_potential || 0).toLocaleString()}+, matched to your profile` : 'Find and track scholarships matched to your profile'}
      />
      <div className="mb-4"><PatriciaPageGuide pageId="scholarships" /></div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}
            className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-blue-600/30 via-blue-500/10 to-transparent border border-blue-500/30 shadow-lg shadow-blue-500/5"
            data-testid="stat-total-scholarships">
            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -translate-y-6 translate-x-6" />
            <Award className="w-7 h-7 text-blue-400 mb-2" />
            <p className="text-white font-bold text-3xl tracking-tight">{stats.total_active}</p>
            <p className="text-blue-300/70 text-sm font-medium mt-1">Total Scholarships</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-emerald-600/30 via-emerald-500/10 to-transparent border border-emerald-500/30 shadow-lg shadow-emerald-500/5"
            data-testid="stat-total-value">
            <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full -translate-y-6 translate-x-6" />
            <DollarSign className="w-7 h-7 text-emerald-400 mb-2" />
            <p className="text-white font-bold text-3xl tracking-tight">${Math.round(stats.total_value_potential / 1000)}K+</p>
            <p className="text-emerald-300/70 text-sm font-medium mt-1">Total Value</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
            className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-cyan-600/30 via-cyan-500/10 to-transparent border border-cyan-500/30 shadow-lg shadow-cyan-500/5"
            data-testid="stat-avg-award">
            <div className="absolute top-0 right-0 w-20 h-20 bg-cyan-500/10 rounded-full -translate-y-6 translate-x-6" />
            <TrendingUp className="w-7 h-7 text-cyan-400 mb-2" />
            <p className="text-white font-bold text-3xl tracking-tight">${stats.average_award?.toLocaleString()}</p>
            <p className="text-cyan-300/70 text-sm font-medium mt-1">Avg Award</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
            className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-purple-600/30 via-purple-500/10 to-transparent border border-purple-500/30 shadow-lg shadow-purple-500/5"
            data-testid="stat-states-covered">
            <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full -translate-y-6 translate-x-6" />
            <MapPin className="w-7 h-7 text-purple-400 mb-2" />
            <p className="text-white font-bold text-3xl tracking-tight">{Object.keys(stats.state_coverage || {}).length}</p>
            <p className="text-purple-300/70 text-sm font-medium mt-1">States Covered</p>
          </motion.div>
        </div>
      )}

      {/* Disclaimer (compact) */}
      <div className="mb-4 flex items-center gap-2 group relative" data-testid="scholarship-disclaimer-top">
        <AlertCircle className="w-3.5 h-3.5 text-amber-500/60 shrink-0" />
        <p className="text-amber-400/50 text-[10px]">Deadlines & details may change. <span className="underline decoration-dotted cursor-help" title="Deadlines, eligibility requirements, award amounts, and application links are subject to change at any time. Some scholarships recur annually and dates shown may be approximate. Always verify current details directly with the scholarship provider before applying. Agentic FuturePath AI is not responsible for changes to third-party scholarship programs or expired links.">Always verify with providers</span> before applying.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-800 pb-3">
        {[
          { key: 'search', label: 'Search & Browse', icon: Search },
          { key: 'recommended', label: 'For You', icon: Sparkles },
          { key: 'saved', label: `Saved (${Object.keys(savedMap).length})`, icon: Bookmark },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all ${
                activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-300 hover:text-white'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <Icon className="w-4 h-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <>
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-4">
            <GlassCard>
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 z-10" />
                  <Input
                    placeholder="Search scholarships (e.g., STEM, nursing, first generation...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-dark pl-11"
                    data-testid="scholarship-search-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="btn-primary whitespace-nowrap" data-testid="search-btn">
                  {loading ? <LoadingSpinner size="sm" /> : 'Search'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)}
                  className="border-slate-700 text-slate-300 whitespace-nowrap" data-testid="toggle-filters">
                  <Filter className="w-4 h-4 mr-2" /> Filters {showFilters ? <ChevronDown className="w-3 h-3 ml-1 rotate-180" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                </Button>
              </div>

              {/* Expanded Filters */}
              <AnimatePresence>
                {showFilters && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t border-slate-800">
                      <div>
                        <label className="text-slate-300 text-xs mb-1 block">State</label>
                        <select value={filterState} onChange={(e) => setFilterState(e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2" data-testid="filter-state">
                          <option value="">All States</option>
                          {US_STATES.filter(s => s !== 'ALL').map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-slate-300 text-xs mb-1 block">Type</label>
                        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2" data-testid="filter-type">
                          {SCHOLARSHIP_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-slate-300 text-xs mb-1 block">School Type</label>
                        <select value={filterSchoolType} onChange={(e) => setFilterSchoolType(e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2" data-testid="filter-school">
                          <option value="">Any</option>
                          <option value="4_year">4-Year College</option>
                          <option value="2_year">2-Year / Community</option>
                          <option value="trade">Trade / Vocational</option>
                          <option value="graduate">Graduate</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-slate-300 text-xs mb-1 block">Min GPA</label>
                        <Input type="number" step="0.1" min="0" max="4.0" placeholder="e.g. 3.0" value={filterGpa}
                          onChange={(e) => setFilterGpa(e.target.value)} className="input-dark" data-testid="filter-gpa" />
                      </div>
                      <div>
                        <label className="text-slate-300 text-xs mb-1 block">Sort By</label>
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg p-2" data-testid="sort-by">
                          {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end mt-3">
                      <Button type="button" size="sm" onClick={() => { setFilterState(''); setFilterType(''); setFilterSchoolType(''); setFilterGpa(''); setSortBy('relevance'); }}
                        variant="ghost" className="text-slate-300 text-xs">
                        Clear Filters
                      </Button>
                      <Button type="submit" size="sm" className="ml-2 btn-primary text-xs">Apply Filters</Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Quick Type Tags */}
              <div className="flex flex-wrap gap-2 mt-3">
                {SCHOLARSHIP_TYPES.slice(1, 8).map(t => (
                  <button key={t.key} onClick={() => { setFilterType(t.key); fetchScholarships(1); }}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${filterType === t.key ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </GlassCard>
          </form>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          ) : (
            <>
              <p className="text-slate-300 text-sm mb-4">{totalResults} scholarships found</p>
              <div className="grid md:grid-cols-2 gap-4" data-testid="scholarship-results">
                {scholarships.map(sch => (
                  <ScholarshipCard key={sch.id} sch={sch} savedMap={savedMap}
                    onSave={handleSave} onUnsave={handleUnsave}
                    onSelect={setSelectedScholarship} onTrack={handleTrack} />
                ))}
              </div>
              {scholarships.length === 0 && !loading && (
                <GlassCard className="text-center py-12">
                  <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-300">No scholarships found. Try adjusting your filters.</p>
                </GlassCard>
              )}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  <Button disabled={page <= 1} variant="outline" size="sm" onClick={() => fetchScholarships(page - 1)}
                    className="border-slate-700 text-slate-300">Previous</Button>
                  <span className="text-slate-300 text-sm self-center">Page {page} of {totalPages}</span>
                  <Button disabled={page >= totalPages} variant="outline" size="sm" onClick={() => fetchScholarships(page + 1)}
                    className="border-slate-700 text-slate-300">Next</Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Recommended Tab */}
      {activeTab === 'recommended' && (
        <>
          <div className="mb-4 p-4 bg-gradient-to-r from-amber-500/10 to-purple-500/10 rounded-xl border border-amber-500/20">
            <p className="text-amber-300 text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Scholarships ranked by how well they match your profile, interests, GPA, grade level, and career goals.
            </p>
          </div>
          {loading ? (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="recommended-results">
              {scholarships.map(sch => (
                <ScholarshipCard key={sch.id} sch={sch} savedMap={savedMap}
                  onSave={handleSave} onUnsave={handleUnsave}
                  onSelect={setSelectedScholarship} onTrack={handleTrack} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Saved Tab */}
      {activeTab === 'saved' && (
        <>
          {savedList.length === 0 ? (
            <GlassCard className="text-center py-12">
              <Bookmark className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-white font-semibold mb-1">No saved scholarships yet</p>
              <p className="text-slate-300 text-sm mb-4">Browse and save scholarships to track your applications here.</p>
              <Button onClick={() => setActiveTab('search')} className="btn-primary">
                <Search className="w-4 h-4 mr-2" /> Browse Scholarships
              </Button>
            </GlassCard>
          ) : (
            <>
              {/* Status summary */}
              <div className="flex gap-3 mb-4 flex-wrap">
                {TRACK_STATUSES.map(ts => {
                  const count = savedList.filter(s => (s.tracking?.status || 'saved') === ts.key).length;
                  return (
                    <div key={ts.key} className={`px-3 py-2 rounded-lg bg-${ts.color}-500/10 text-${ts.color}-400 text-xs font-medium`}>
                      {ts.label}: {count}
                    </div>
                  );
                })}
              </div>
              <div className="grid md:grid-cols-2 gap-4" data-testid="saved-results">
                {savedList.map(sch => (
                  <ScholarshipCard key={sch.id} sch={sch} savedMap={savedMap}
                    onSave={handleSave} onUnsave={handleUnsave}
                    onSelect={setSelectedScholarship} onTrack={handleTrack} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Footer Disclaimer (compact) */}
      <div className="mt-6 flex items-center gap-2" data-testid="scholarship-disclaimer">
        <AlertCircle className="w-3 h-3 text-amber-500/40 shrink-0" />
        <p className="text-amber-400/40 text-[10px]">Scholarship data is for discovery only. <span className="underline decoration-dotted cursor-help" title="Scholarship deadlines and links may be outdated or expired. Agentic FuturePath AI provides this data for discovery purposes only. Always confirm eligibility, deadlines, and application details directly on the scholarship provider's official website before applying.">Verify all details</span> with providers.</p>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedScholarship && (
          <ScholarshipDetailModal
            sch={selectedScholarship}
            savedMap={savedMap}
            onClose={() => setSelectedScholarship(null)}
            onSave={handleSave}
            onUnsave={handleUnsave}
            onTrack={handleTrack}
          />
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
